import React, { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AppButton } from '@/components/atoms';
import { Plus } from '@/components/atoms/AppIcon';
import { AdminClinicsListTemplate } from '@/components/templates';
import { ClinicsFilterPanel, ClinicsTableSection } from '@/components/organisms';
import {
  getAllClinics,
  getClinics,
  ClinicsListParams,
  downloadAllClinicsCsv,
  downloadClinicsCsv,
} from '@/services/admin/clinics';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';

const DEFAULT_PER_PAGE = 20;

type SortField = 'clinics.clinic_number' | 'clinics.available_from';
type SortDir = 'ascend' | 'descend';

type SortState = {
  field: SortField;
  dir: SortDir;
};

type ClinicFilterState = {
  searchClinicNumber: string;
  availableDate: string;
  siteTypes: string[];
};

const getFiltersFromSearchParams = (searchParams: URLSearchParams): ClinicFilterState => {
  const siteTypesFromArray = searchParams.getAll('site_types').filter(Boolean);
  const siteTypesFromCsv = (searchParams.get('site_types') ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  const siteTypes = siteTypesFromArray.length > 0 ? siteTypesFromArray : siteTypesFromCsv;

  return {
    searchClinicNumber: searchParams.get('search_clinic_number') ?? '',
    availableDate: searchParams.get('available_date') ?? '',
    siteTypes,
  };
};

const getSortStateFromSearchParams = (
  searchParams: URLSearchParams
): SortState | null => {
  const sortBy = searchParams.get('sort_by');
  const order = searchParams.get('order');
  const isValidSortField = (value: string | null): value is SortField =>
    value === 'clinics.clinic_number' || value === 'clinics.available_from';

  if (!isValidSortField(sortBy)) return null;
  if (order !== 'asc' && order !== 'desc') return null;
  return { field: sortBy, dir: order === 'asc' ? 'ascend' : 'descend' };
};

const getPageFromSearchParams = (searchParams: URLSearchParams): number => {
  const raw = Number(searchParams.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const SiteAdminClinicsListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : site ?? ''],
    queryFn: () =>
      getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] =
    siteTypesQuery.data?.data?.site_types ?? [];

  const canUseSiteTypeFeatures = tenantSiteTypeList.length > 1;

  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError]);

  const initialFilters = getFiltersFromSearchParams(searchParams);

  const [searchClinicNumber, setSearchClinicNumber] = useState<string>(initialFilters.searchClinicNumber);
  const [availableDate, setAvailableDate] = useState<string>(initialFilters.availableDate);
  const [siteTypes, setSiteTypes] = useState<string[]>(initialFilters.siteTypes);

  const [appliedSearchClinicNumber, setAppliedSearchClinicNumber] = useState<string>(initialFilters.searchClinicNumber);
  const [appliedAvailableDate, setAppliedAvailableDate] = useState<string>(initialFilters.availableDate);
  const [appliedSiteTypes, setAppliedSiteTypes] = useState<string[]>(initialFilters.siteTypes);

  const tenantSiteTypes = useMemo(
    () => tenantSiteTypeList.map(t => t.site_type).filter(Boolean) as string[],
    [tenantSiteTypeList]
  );
  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => siteTypes.includes(st));

  const [page, setPage] = useState<number>(getPageFromSearchParams(searchParams));
  const [sortState, setSortState] = useState<SortState | null>(getSortStateFromSearchParams(searchParams));

  useEffect(() => {
    const nextFilters = getFiltersFromSearchParams(searchParams);
    const nextPage = getPageFromSearchParams(searchParams);
    const nextSortState = getSortStateFromSearchParams(searchParams);

    setSearchClinicNumber(nextFilters.searchClinicNumber);
    setAvailableDate(nextFilters.availableDate);
    setSiteTypes(nextFilters.siteTypes);

    setAppliedSearchClinicNumber(nextFilters.searchClinicNumber);
    setAppliedAvailableDate(nextFilters.availableDate);
    setAppliedSiteTypes(nextFilters.siteTypes);

    setPage(nextPage);
    setSortState(nextSortState);
  }, [searchParams]);

  const params: ClinicsListParams = useMemo(
    () => ({
      search_clinic_number: appliedSearchClinicNumber || undefined,
      available_date: appliedAvailableDate || undefined,
      site_types: canUseSiteTypeFeatures
        ? appliedSiteTypes.length > 0
          ? appliedSiteTypes
          : undefined
        : undefined,
      sort: sortState
        ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc']
        : ['clinics.created_at', 'asc'],
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [
      appliedSearchClinicNumber,
      appliedAvailableDate,
      appliedSiteTypes,
      canUseSiteTypeFeatures,
      sortState,
      page,
    ]
  );

  const clinicsQuery = useQuery({
    queryKey: ['site-admin', 'clinics', canUseSiteTypeFeatures ? 'all' : site, params],
    queryFn: async () => {
      if (!site) {
        return {
          success: false,
          data: { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0, data: [] },
        };
      }

      // Even if the user is a full admin, when they are in a specific site admin portal
      // (/{site}/admin/...), we must scope the list to that site only.
      // Cross-site listing is allowed only on the super-admin route.
      if (canUseSiteTypeFeatures) {
        return getAllClinics(site, params);
      }

      return getClinics(site, params);
    },
    enabled: !!site && !isAuthLoading,
    /** Refetch on every 検索 click (override global staleTime). */
    staleTime: 0,
  });

  const rows = clinicsQuery.data?.data?.data || [];
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
  });

  useEffect(() => {
    if (clinicsQuery.data?.meta) {
      setMeta({
        current_page: clinicsQuery.data?.meta.current_page || 1,
        last_page: clinicsQuery.data?.meta.last_page,
        per_page: clinicsQuery.data?.meta.per_page,
        total: clinicsQuery.data?.meta.total,
      });
    }
  }, [clinicsQuery.data]);

  const handleSearch = () => {
    if (!site) return;
    const nextParams = new URLSearchParams();
    if (searchClinicNumber.trim()) nextParams.set('search_clinic_number', searchClinicNumber.trim());
    if (availableDate.trim()) nextParams.set('available_date', availableDate.trim());
    if (canUseSiteTypeFeatures) {
      siteTypes
        .map(v => v.trim())
        .filter(Boolean)
        .forEach(v => nextParams.append('site_types', v));
    }
    nextParams.set('page', '1');
    if (sortState) {
      nextParams.set('sort_by', sortState.field);
      nextParams.set('order', sortState.dir === 'ascend' ? 'asc' : 'desc');
    }

    flushSync(() => {
      setSearchParams(nextParams);
      setPage(1);
      setAppliedSearchClinicNumber(searchClinicNumber);
      setAppliedAvailableDate(availableDate);
      setAppliedSiteTypes(siteTypes);
    });
    void queryClient.refetchQueries({
      queryKey: ['site-admin', 'clinics', canUseSiteTypeFeatures ? 'all' : site],
      type: 'active',
    });
  };

  const setSort = (field: SortField | null, dir: SortDir | null) => {
    const nextParams = new URLSearchParams(searchParams);

    if (!field || !dir) {
      nextParams.delete('sort_by');
      nextParams.delete('order');
      nextParams.set('page', '1');
      setSearchParams(nextParams);
      return;
    }

    nextParams.set('sort_by', field);
    nextParams.set('order', dir === 'ascend' ? 'asc' : 'desc');
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleChangePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  const handleCsv = async () => {
    if (!site) return;
    try {
      if (canUseSiteTypeFeatures) {
        await downloadAllClinicsCsv(site, params);
      } else {
        await downloadClinicsCsv(site, params);
      }
    } catch (e) {
      toast.error('CSVのダウンロードに失敗しました。');
    }
  };

  return (
    <>
      <AdminClinicsListTemplate
        breadcrumbItems={[{ title: '治療院' }]}
        filter={
          <ClinicsFilterPanel
            canUseSiteTypeFeatures={canUseSiteTypeFeatures}
            tenants={tenantSiteTypeList}
            siteTypes={siteTypes}
            hasAllSelected={hasAllSelected}
            onChangeSiteTypes={setSiteTypes}
            searchClinicNumber={searchClinicNumber}
            onChangeSearchClinicNumber={setSearchClinicNumber}
            availableDate={availableDate}
            onChangeAvailableDate={setAvailableDate}
            onSearch={handleSearch}
            onCsv={handleCsv}
          />
        }
        betweenFilterAndTable={
          site ? (
            <div className="mb-4">
              <Link to={`/${site}/admin/clinics/create`}>
                <AppButton icon={<Plus />} type="primary" className="py-1 my-1 btn-ra2-hover">
                  新規
                </AppButton>
              </Link>
            </div>
          ) : undefined
        }
        table={
          <ClinicsTableSection
            site={site!}
            rows={rows}
            loading={clinicsQuery.isLoading}
            meta={meta}
            defaultPerPage={DEFAULT_PER_PAGE}
            canUseSiteTypeFeatures={canUseSiteTypeFeatures}
            tenantNameBySiteType={tenantNameBySiteType}
            onSort={(f, d) => setSort(f as SortField | null, d as SortDir | null)}
            onChangePage={handleChangePage}
          />
        }
      />
    </>
  );
};

export default SiteAdminClinicsListPage;
